import { useReactiveVar } from '@apollo/client';

import { useMailThreadIdByGmailQuery } from 'core/graphql';
import { userStateVar } from 'core/state';
import { MAIL_ROLES, visibleFor } from 'core/hooks/Palette/paletteRoles';
import { UserRole } from 'types';

import { mailHref } from './mailModel';

interface IMailThreadHrefArgs {
  mailboxEmail?: string | null;
  /** Gmail thread id (у диалога коммуникатора — `externalId`). */
  gmailThreadId?: string | null;
  /** Тема — для запасной ссылки поиском. */
  subject?: string | null;
}

/**
 * ТЗ 0122 — «Открыть в почте» у Gmail-треда коммуникатора: точная ссылка
 * на тред почты (`mailThreadByGmailId`), а пока его нет в индексе почты —
 * ящик с поиском по теме. Роли без почты (`MAIL_ROLES`) ссылки не получают.
 */
export const useMailThreadHref = ({
  mailboxEmail,
  gmailThreadId,
  subject,
}: IMailThreadHrefArgs): string | null => {
  const { me } = useReactiveVar(userStateVar);
  const allowed = visibleFor((me?.roles ?? []) as UserRole[], MAIL_ROLES);
  const ready = allowed && Boolean(mailboxEmail) && Boolean(gmailThreadId);
  const { data } = useMailThreadIdByGmailQuery({
    variables: {
      mailboxEmail: mailboxEmail || '',
      gmailThreadId: gmailThreadId || '',
    },
    skip: !ready,
    fetchPolicy: 'cache-first',
  });
  if (!allowed || !mailboxEmail) return null;
  const threadId = data?.mailThreadByGmailId;
  return threadId
    ? mailHref({ boxEmail: mailboxEmail, view: 'all', thread: threadId })
    : mailHref({ boxEmail: mailboxEmail, view: 'all', search: subject || '' });
};
